import { useEffect, useRef, useState } from "react";
import { Move, PartyPopper, ImagePlus } from "lucide-react";
import { InviteText } from "./InviteText";
import { PAD, placement, posOf } from "../lib/inviteLayout";

const round1 = (v) => Math.round(v * 10) / 10;
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));
const editCls = "pointer-events-auto cursor-move touch-none rounded-md outline-dashed outline-1 outline-offset-4 outline-white/70 hover:outline-2 hover:outline-white";

const Placeholder = ({ hint }) => (
  <div className="confetti-bg-white flex aspect-[4/5] w-full flex-col items-center justify-center gap-3 bg-slate-50 text-slate-400" data-testid="invite-image-placeholder">
    {hint && (
      <>
        <ImagePlus className="h-10 w-10" />
        <span className="text-sm font-semibold">Upload an image to make it yours</span>
      </>
    )}
  </div>
);

const useRsvpDrag = (frame, onMove) => {
  const [dragging, setDragging] = useState(false);
  const moveTo = (e) => {
    const r = frame.current.getBoundingClientRect();
    onMove(round1(clamp(((e.clientX - r.left) / r.width) * 100, 4, 96)), round1(clamp(((e.clientY - r.top) / r.height) * 100, 4, 96)));
  };
  const handlers = {
    onPointerDown: (e) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(true);
    },
    onPointerMove: (e) => dragging && moveTo(e),
    onPointerUp: () => setDragging(false),
    onPointerCancel: () => setDragging(false),
  };
  return [dragging, handlers];
};

const useTextDrag = (frame, onTextMove) => {
  const [drag, setDrag] = useState(null);
  useEffect(() => {
    if (!drag) return undefined;
    const move = (e) => {
      const fr = frame.current.getBoundingClientRect();
      const x = clamp(((e.clientX - drag.dx - fr.left) / fr.width) * 100, 0, 100 - drag.w);
      const y = clamp(((e.clientY - drag.dy - fr.top) / fr.height) * 100, 0, 100 - drag.h);
      onTextMove({ [drag.kind]: { x: round1(x), y: round1(y) } });
    };
    const stop = () => setDrag(null);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
    };
  }, [drag, frame, onTextMove]);
  return setDrag;
};

export const InviteCanvas = ({ event, imageSrc, onRsvp, onMove, onTextMove, editable = false }) => {
  const frame = useRef(null);
  const els = useRef({});
  const [pinDragging, pinHandlers] = useRsvpDrag(frame, onMove);
  const setDrag = useTextDrag(frame, onTextMove);
  const { onImage, below } = placement(event);
  const flow = onImage.filter((k) => !posOf(event, k));
  const placed = onImage.filter((k) => posOf(event, k));

  const startDrag = (kind) => (e) => {
    e.preventDefault();
    const fr = frame.current.getBoundingClientRect();
    const toPct = (el) => {
      const r = el.getBoundingClientRect();
      return { x: round1(((r.left - fr.left) / fr.width) * 100), y: round1(((r.top - fr.top) / fr.height) * 100), w: (r.width / fr.width) * 100, h: (r.height / fr.height) * 100 };
    };
    if (flow.length) onTextMove(Object.fromEntries(flow.map((k) => { const { x, y } = toPct(els.current[k]); return [k, { x, y }]; })));
    const r = e.currentTarget.getBoundingClientRect();
    const p = toPct(e.currentTarget);
    setDrag({ kind, dx: e.clientX - r.left, dy: e.clientY - r.top, w: p.w, h: p.h });
  };
  const textProps = (kind) => ({
    kind,
    event,
    ref: (el) => { els.current[kind] = el; },
    ...(editable ? { onPointerDown: startDrag(kind), className: editCls } : {}),
  });

  return (
    <div
      data-testid={editable ? "live-preview-container" : "invite-card"}
      style={{ containerType: "inline-size" }}
      className="overflow-hidden rounded-[1.75rem] bg-white shadow-[0_30px_70px_-25px_rgba(15,23,42,0.45)] ring-1 ring-slate-900/5"
    >
      <div ref={frame} className="relative w-full select-none" data-testid="invite-image-frame">
        {imageSrc ? (
          <img data-testid="invite-image" src={imageSrc} alt={event.occasion} draggable={false} className="block h-auto w-full" />
        ) : (
          <Placeholder hint={editable} />
        )}
        {flow.length > 0 && (
          <div data-testid="invite-details-overlay" className="overlay-scrim pointer-events-none absolute inset-x-0 top-0" style={{ padding: `${PAD}cqw ${PAD}cqw 13cqw` }}>
            {flow.map((k, i) => <InviteText key={k} overlay first={i === 0} {...textProps(k)} />)}
          </div>
        )}
        {placed.map((k) => {
          const { x, y } = posOf(event, k);
          return (
            <InviteText
              key={k}
              overlay
              {...textProps(k)}
              className={`${editable ? editCls : "pointer-events-none"} absolute`}
              style={{ left: `${x}%`, top: `${y}%`, width: "max-content", maxWidth: `${Math.max(100 - x - PAD, 20)}cqw`, textShadow: "0 1px 3px rgba(0,0,0,.55), 0 2px 16px rgba(0,0,0,.5)" }}
            />
          );
        })}
        <button
          type="button"
          data-testid={editable ? "draggable-rsvp-pin" : "guest-rsvp-button"}
          style={{ left: `${event.rsvp_x}%`, top: `${event.rsvp_y}%` }}
          className={`absolute z-10 inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-xl border-2 border-white/80 bg-blue-700 px-6 py-3 text-sm font-bold uppercase tracking-[0.18em] text-white shadow-xl transition-[transform,background-color] duration-200 hover:bg-blue-800 hover:text-white ${
            editable ? "cursor-grab touch-none active:cursor-grabbing" : "rsvp-pulse hover:scale-105"
          } ${pinDragging ? "scale-110" : ""}`}
          {...(editable ? pinHandlers : { onClick: onRsvp })}
        >
          {editable ? <Move className="h-4 w-4" /> : <PartyPopper className="h-4 w-4" />}
          RSVP
          {editable && (
            <span data-testid="rsvp-pin-coordinates" className="absolute -top-8 left-1/2 -translate-x-1/2 rounded-full bg-slate-900 px-2 py-0.5 font-mono text-[10px] tracking-normal text-white">
              {event.rsvp_x}% · {event.rsvp_y}%
            </span>
          )}
        </button>
      </div>
      {below.length > 0 && (
        <div data-testid="invite-details-below" style={{ padding: `${PAD}cqw` }}>
          {below.map((k, i) => <InviteText key={k} kind={k} event={event} overlay={false} first={i === 0} />)}
        </div>
      )}
    </div>
  );
};
